// ── Country features (client single point) — multi-tenant package "cpackdocs" ───────────────────
// Mirror of server/src/services/countryFeature.ts for what the browser prints: land measure units,
// address pieces, government portals, the UI languages offered, statutory book names. The values ride
// on /api/me as `me.countryFeatures` (the ACTIVE company's country, else the workspace's); a no-login
// page (DD share link, client portal) hands in the copy its own bootstrap fetched. No country content
// lives here: a field the country does not carry is empty and the screen degrades (m² only, no link…).
import { useMemo } from 'react';
import { useStore } from '../store';
import { fmtNumber } from './region';

export type LandAreaUnit = { key: string; label: string; m2: number; hint?: string };
export type AddressPart = { key: string; en: string; local: string };
export type GovPortal = { key: string; label: string; url: string };
export type CountryFeatures = {
  country: string;
  landAreaUnits: LandAreaUnit[];
  addressParts: AddressPart[];
  govPortals: GovPortal[];
  uiLanguages: string[];
  statutoryBooks: Record<string, string>;
};

/** The UI language bundles this build ships (src/i18n) — offered until the server says otherwise. */
export const SHIPPED_UI_LANGUAGES: readonly string[] = ['en', 'zh', 'th'];

let override: Partial<CountryFeatures> | null = null;
/** A no-login page's own copy (used only while the store holds no `me.countryFeatures`). */
export const setCountryFeaturesOverride = (f: Partial<CountryFeatures> | null | undefined): void => { override = f || null; };

const arr = <T,>(v: any): T[] => (Array.isArray(v) ? v : []);
const normalise = (f: Partial<CountryFeatures> | null | undefined): CountryFeatures => {
  const langs = arr<string>(f?.uiLanguages).filter(l => SHIPPED_UI_LANGUAGES.includes(l));
  return {
    country: String(f?.country || '').toUpperCase(),
    landAreaUnits: arr<LandAreaUnit>(f?.landAreaUnits).filter(u => u && u.key && Number(u.m2) > 0),
    addressParts: arr<AddressPart>(f?.addressParts),
    govPortals: arr<GovPortal>(f?.govPortals),
    uiLanguages: langs.length ? langs : [...SHIPPED_UI_LANGUAGES],
    statutoryBooks: (f?.statutoryBooks && typeof f.statutoryBooks === 'object') ? f.statutoryBooks : {},
  };
};

/** The features as the store holds them right now (empty fields before /api/me has answered). */
export function countryFeatures(): CountryFeatures {
  let f: Partial<CountryFeatures> | undefined;
  try { f = useStore.getState().me?.countryFeatures; } catch { /* store not ready */ }
  return normalise(f || override || null);
}
/** Hook form, for components that should re-render when /api/me lands. */
export function useCountryFeatures(): CountryFeatures {
  const raw = useStore(s => s.me?.countryFeatures);
  return useMemo(() => normalise(raw || override || null), [raw]);
}

// ── Land measure ────────────────────────────────────────────────────────────────────────────────
export const landAreaUnits = (): LandAreaUnit[] => countryFeatures().landAreaUnits;
/** Square metres from a record's local units, 2 dp ('' when nothing is entered or the country has no units). */
export function landAreaM2(rec: any): string {
  const v = landAreaUnits().reduce((s, u) => s + (parseFloat(rec?.[u.key]) || 0) * u.m2, 0);
  return v ? (Math.round(v * 100) / 100).toFixed(2) : '';
}
/** "1 Rai = 1,600 m², 1 Ngan = 400 m², …" — the conversion hint beside an auto-calculated m² box. */
export const landAreaHint = (): string => landAreaUnits().map(u => `1 ${u.label} = ${fmtNumber(u.m2)} m²`).join(', ');

// ── UI languages ────────────────────────────────────────────────────────────────────────────────
/** The UI languages a picker offers: the workspace's list, plus the one in use (so it never vanishes). */
export function offeredUiLanguages(current?: string): string[] {
  const list = countryFeatures().uiLanguages;
  return current && SHIPPED_UI_LANGUAGES.includes(current) && !list.includes(current) ? [...list, current] : list;
}

// ── Statutory book names ────────────────────────────────────────────────────────────────────────
/** The statutory local name of a book kind ('' when the country prescribes none). */
export const statutoryBookName = (kind: string): string => String(countryFeatures().statutoryBooks[kind] || '');
